import { useState, useEffect, useCallback, useMemo } from 'react';
import { Clock, Plus, X, Bell, RefreshCw, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import PlayerDetailModal from '../components/PlayerDetailModal';

// ─── Page ──────────────────────────────────────────────────────────────────
export default function Waitlist() {
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const loadGames = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/waitlist/games');
      setGames(data.games || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEntries = useCallback(async (gameId) => {
    if (!gameId) return;
    setEntriesLoading(true);
    try {
      const { data } = await api.get(`/admin/waitlist/entries/${gameId}`);
      setEntries(data.entries || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load waitlist');
    } finally {
      setEntriesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  useEffect(() => {
    if (selectedGame) loadEntries(selectedGame.id);
  }, [selectedGame, loadEntries]);

  const handleRefresh = useCallback(() => {
    loadGames();
    if (selectedGame) loadEntries(selectedGame.id);
  }, [loadGames, loadEntries, selectedGame]);

  const handleCall = async (entry) => {
    if (!selectedGame || !entry.memberNumber) {
      toast.error('No member number — cannot notify walk-in');
      return;
    }
    try {
      await api.post('/admin/waitlist/call', {
        gameId: selectedGame.id,
        memberNumber: entry.memberNumber,
      });
      toast.success(`${entry.playerName} has been notified`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to call');
    }
  };

  const handleRenew = async (entry) => {
    if (!selectedGame) return;
    try {
      await api.post('/admin/waitlist/renew', {
        gameId: selectedGame.id,
        waitId: entry.waitId || undefined,
        memberNumber: entry.memberNumber || undefined,
        transactionId: entry.transactionId || undefined,
      });
      toast.success(`${entry.playerName} moved to end of list`);
      loadEntries(selectedGame.id);
    } catch (err) {
      toast.error('Failed to renew');
    }
  };

  const handleRemove = async (entry) => {
    if (!selectedGame) return;
    if (!window.confirm(`Remove ${entry.playerName} from the waitlist?`)) return;
    try {
      await api.delete('/admin/waitlist/remove', {
        data: {
          gameId: selectedGame.id,
          waitId: entry.waitId || undefined,
          memberNumber: entry.memberNumber || undefined,
          transactionId: entry.transactionId || undefined,
        },
      });
      toast.success(`${entry.playerName} removed`);
      loadEntries(selectedGame.id);
      loadGames();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const handleAdded = useCallback(() => {
    setShowAddModal(false);
    loadGames();
    if (selectedGame) loadEntries(selectedGame.id);
  }, [loadGames, loadEntries, selectedGame]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Clock className="w-6 h-6 text-amber-500" />
            Waitlist Management
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Add members or walk-ins to live game waitlists.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Player
          </button>
        </div>
      </div>

      {/* Game tabs */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-2">
          {games.length === 0 ? (
            <p className="text-gray-400 text-sm">No active games.</p>
          ) : (
            games.map((g) => {
              const active = selectedGame?.id === g.id;
              return (
                <button
                  key={g.id}
                  onClick={() => setSelectedGame(g)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    active
                      ? 'bg-amber-500 text-black font-medium'
                      : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                  }`}
                >
                  {g.gameName}
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      active ? 'bg-black/30 text-black' : 'bg-gray-600 text-gray-200'
                    }`}
                  >
                    {g.waiting}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Entries */}
      {selectedGame ? (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <div className="bg-gray-700/50 px-6 py-3 flex items-center justify-between">
            <h2 className="text-white font-semibold">{selectedGame.gameName}</h2>
            <span className="text-gray-400 text-sm">{entries.length} waiting</span>
          </div>

          {entriesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
            </div>
          ) : entries.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-400">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No players on this waitlist.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-700/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider w-16">Pos</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Player</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {entries.map((entry) => (
                  <tr key={entry.waitId || `${entry.position}-${entry.transactionId || entry.playerName}`} className="hover:bg-gray-700/30">
                    <td className="px-6 py-4">
                      <div className="w-8 h-8 rounded-full bg-amber-500 text-black font-bold flex items-center justify-center text-sm">
                        {entry.position}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {entry.playerId ? (
                        <button
                          onClick={() => setSelectedPlayer(entry)}
                          className="text-left text-white font-medium hover:text-amber-400 transition-colors"
                          title="View player details"
                        >
                          {entry.playerName}
                        </button>
                      ) : (
                        <div className="text-white font-medium">{entry.playerName}</div>
                      )}
                      {entry.memberNumber && (
                        <div className="text-gray-400 text-xs mt-0.5">#{entry.memberNumber}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {entry.isWalkIn ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-orange-500/20 text-orange-400 font-medium">
                          WALK-IN
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400 font-medium">
                          MEMBER
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-300 text-sm">
                      {entry.joinedAt ? new Date(entry.joinedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {entry.memberNumber && (
                          <button
                            onClick={() => handleCall(entry)}
                            className="p-2 text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                            title="Notify player their seat is ready"
                          >
                            <Bell className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRenew(entry)}
                          className="p-2 text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors"
                          title="Move to end of list"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRemove(entry)}
                          className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Remove"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl px-6 py-12 text-center text-gray-400">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Select a game above to view its waitlist.</p>
        </div>
      )}

      {showAddModal && (
        <AddPlayerModal
          games={games}
          defaultGameId={selectedGame?.id}
          onClose={() => setShowAddModal(false)}
          onAdded={handleAdded}
        />
      )}

      <PlayerDetailModal
        visible={!!selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
        playerId={selectedPlayer?.playerId}
        player={selectedPlayer ? { ...selectedPlayer, displayName: selectedPlayer.playerName } : null}
      />
    </div>
  );
}

// ─── Add Player Modal ──────────────────────────────────────────────────────
function AddPlayerModal({ games, defaultGameId, onClose, onAdded }) {
  const [mode, setMode] = useState('member'); // 'member' | 'walkIn'
  const [memberNumber, setMemberNumber] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [seatPreference, setSeatPreference] = useState(0);
  const [selectedGameIds, setSelectedGameIds] = useState(defaultGameId ? [defaultGameId] : []);
  const [submitting, setSubmitting] = useState(false);

  const toggleGame = useCallback((gameId) => {
    setSelectedGameIds((prev) =>
      prev.includes(gameId) ? prev.filter((id) => id !== gameId) : [...prev, gameId]
    );
  }, []);

  const handleSubmit = async () => {
    if (selectedGameIds.length === 0) {
      toast.error('Select at least one game');
      return;
    }
    if (mode === 'member' && !memberNumber.trim()) {
      toast.error('Enter a member number');
      return;
    }
    if (mode === 'walkIn' && !playerName.trim()) {
      toast.error("Enter the player's name");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post('/admin/waitlist/add', {
        gameIds: selectedGameIds,
        memberNumber: mode === 'member' ? memberNumber.trim() : undefined,
        playerName: mode === 'walkIn' ? playerName.trim() : undefined,
        walkIn: mode === 'walkIn' ? true : undefined,
        seatPreference: seatPreference || undefined,
      });

      const ok = data.entries?.length || 0;
      const failed = data.errors?.length || 0;

      if (ok > 0 && failed === 0) {
        toast.success(`Added to ${ok} game${ok > 1 ? 's' : ''}`);
        onAdded();
      } else if (ok > 0 && failed > 0) {
        toast(`Added to ${ok}/${ok + failed} — ${data.errors.map((e) => `${e.gameName}: ${e.error}`).join('; ')}`, {
          icon: '⚠️',
        });
        onAdded();
      } else {
        toast.error(data.errors?.[0]?.error || 'No games added');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add');
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = useMemo(() => {
    const n = selectedGameIds.length;
    return `Add to ${n} game${n === 1 ? '' : 's'}`;
  }, [selectedGameIds.length]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Add to Waitlist</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {/* Mode toggle */}
          <div className="flex bg-gray-900 rounded-lg p-1 mb-4">
            <button
              onClick={() => setMode('member')}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === 'member' ? 'bg-amber-500 text-black' : 'text-gray-300'
              }`}
            >
              Member
            </button>
            <button
              onClick={() => setMode('walkIn')}
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === 'walkIn' ? 'bg-amber-500 text-black' : 'text-gray-300'
              }`}
            >
              Walk-in
            </button>
          </div>

          {/* Input */}
          {mode === 'member' ? (
            <input
              type="text"
              inputMode="numeric"
              placeholder="Member Number"
              value={memberNumber}
              onChange={(e) => setMemberNumber(e.target.value)}
              autoFocus
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 mb-4"
            />
          ) : (
            <input
              type="text"
              placeholder="Player Name or Initials"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              autoFocus
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 mb-4"
            />
          )}

          {/* Seat preference */}
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Seat Preference (optional)
          </label>
          <div className="flex flex-wrap gap-2 mb-4">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <button
                key={n}
                onClick={() => setSeatPreference(n)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors min-w-[44px] ${
                  seatPreference === n
                    ? 'bg-amber-500 text-black font-medium'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {n === 0 ? 'Any' : n}
              </button>
            ))}
          </div>

          {/* Multi-game picker */}
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Games ({selectedGameIds.length} selected)
          </label>
          <div className="bg-gray-900 border border-gray-700 rounded-lg max-h-64 overflow-y-auto">
            {games.length === 0 ? (
              <p className="text-gray-500 text-sm px-4 py-3">No active games available.</p>
            ) : (
              games.map((game) => {
                const checked = selectedGameIds.includes(game.id);
                return (
                  <label
                    key={game.id}
                    className="flex items-center px-4 py-3 hover:bg-gray-800/50 cursor-pointer border-b border-gray-800 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleGame(game.id)}
                      className="w-4 h-4 rounded text-amber-500 bg-gray-700 border-gray-600 focus:ring-amber-500 focus:ring-offset-gray-900"
                    />
                    <span className="ml-3 text-white text-sm flex-1">{game.gameName}</span>
                    <span className="text-gray-500 text-xs">{game.waiting} waiting</span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-6 py-4 border-t border-gray-700">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || selectedGameIds.length === 0}
            className="flex-[2] px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-black"></div>
            ) : (
              submitLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
